import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SectionTextSettingsCard from '@/components/SectionTextSettingsCard';
import { toast } from 'sonner';
import { GripVertical, Mail, MapPin, MessageCircle, Phone, Plus, Trash2 } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  getContactInfo,
  updateContactInfo,
  updateLeadNotificationEmail,
  UNIT_FIELDS,
  MAX_CONTACT_UNITS,
  MAX_HEADING_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  MAX_UNIT_FIELD_LENGTH,
  MAX_UNIT_NAME_LENGTH,
  DEFAULT_CONTACT_HEADING,
  type ContactUnit,
  type UnitField,
  type UnitFieldData,
  type ContactInfo,
} from '@/services/contactInfoApi';

/**
 * Contact Section manager — content home for the public contact section.
 * Tab 1 "Contact details": block heading, show/hide toggle, description, and
 *   contact UNITS —
 *   each unit = a name (person / management, the group title on the website)
 *   + a list of field rows the artist drags into order INSIDE the unit. Only
 *   filled rows render on the website; the name shows ONCE as the group
 *   title. "Add more" appends another unit (max 8).
 * Tab 2 "Contact form": show/hide toggle for the enquiry form (off hides it
 *   and stops accepting submissions) + destination for new-lead notification
 *   emails.
 * Rendered live on the public website and in the live preview via the
 * `contact-info:update` socket event. The lead inbox lives under
 * "Website Leads".
 */

const UNIT_FIELD_META: Record<UnitField, { label: string; icon: React.ElementType; placeholder: string; inputType?: string }> = {
  phone: { label: 'Phone', icon: Phone, placeholder: '+91 98765 43210', inputType: 'tel' },
  email: { label: 'Email', icon: Mail, placeholder: 'bookings@artist.com', inputType: 'email' },
  address: { label: 'Address', icon: MapPin, placeholder: 'Mumbai, India' },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, placeholder: '+91 98765 43210', inputType: 'tel' },
};

/**
 * Local shapes — rows carry client-only ids so @dnd-kit/sortable can track
 * them. Ids are stripped before every API call.
 */
type FieldRow = UnitFieldData & { rowId: string };
type UnitRow = { id: string; name: string; fields: FieldRow[] };

let idCounter = 0;
const nextId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${++idCounter}`;

/**
 * Editor always shows ALL 4 field rows per unit — missing types are appended
 * (canonical order) as empty rows so the artist sees every slot and can fill
 * / reorder freely. Empty rows are dropped server-side, so this is purely a
 * UI affordance.
 */
const toRows = (units: ContactUnit[]): UnitRow[] =>
  units.map((unit) => {
    const rows: FieldRow[] = (unit.fields || []).map((f) => ({ ...f, rowId: nextId('field') }));
    const present = new Set(rows.map((r) => r.type));
    for (const type of UNIT_FIELDS) {
      if (!present.has(type)) {
        rows.push({ type, value: '', rowId: nextId('field') });
      }
    }
    return { id: nextId('unit'), name: unit.name || '', fields: rows };
  });

const toPayload = (rows: UnitRow[]): ContactUnit[] =>
  rows.map((unit) => ({
    name: unit.name,
    fields: unit.fields.map(({ type, value }) => ({ type, value })),
  }));

/** Saved heading with the website's default applied (empty = no override). */
const effectiveHeading = (doc: ContactInfo | null): string =>
  (doc?.heading || '').trim() || DEFAULT_CONTACT_HEADING;

/** Sortable field row INSIDE a unit — grip-drag to reorder. No delete:
 *  unfilled fields simply don't render on the website. */
const SortableFieldRow: React.FC<{
  row: FieldRow;
  unitIndex: number;
  onValueChange: (unitIndex: number, rowId: string, value: string) => void;
}> = ({ row, unitIndex, onValueChange }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.rowId });
  const meta = UNIT_FIELD_META[row.type];
  const Icon = meta.icon;

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.6 : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <div className="flex items-center gap-1.5 select-none cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
        <GripVertical className="size-4 text-muted-foreground" />
        <Icon className="size-4 text-muted-foreground" />
      </div>
      <Input
        type={meta.inputType || 'text'}
        value={row.value}
        maxLength={MAX_UNIT_FIELD_LENGTH}
        onChange={(e) => onValueChange(unitIndex, row.rowId, e.target.value)}
        placeholder={meta.label}
        className="h-8 flex-1"
        aria-label={`${meta.label} (unit ${unitIndex + 1})`}
      />
    </div>
  );
};

/** Sortable unit card — grip-drag to reorder units; inner DnD reorders fields. */
const SortableUnitCard: React.FC<{
  unit: UnitRow;
  index: number;
  onNameChange: (index: number, value: string) => void;
  onFieldValueChange: (unitIndex: number, rowId: string, value: string) => void;
  onFieldReorder: (unitIndex: number, oldIndex: number, newIndex: number) => void;
  onRemove: (index: number) => void;
}> = ({ unit, index, onNameChange, onFieldValueChange, onFieldReorder, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: unit.id });
  // Own sensors for the inner field-reorder DndContext (hooks must live here,
  // not at module level — each unit card carries its own field DndContext).
  const fieldSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = unit.fields.findIndex((f) => f.rowId === active.id);
    const newIndex = unit.fields.findIndex((f) => f.rowId === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onFieldReorder(index, oldIndex, newIndex);
  };

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.6 : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="rounded-lg border border-border p-3 bg-background">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 select-none cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
          <GripVertical className="size-4 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground">
            {unit.name.trim() || `Unit ${index + 1}`}
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 text-destructive hover:text-destructive"
          onClick={() => onRemove(index)}
          aria-label="Remove unit"
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>

      {/* Name — person / management; the group title on the website (shown ONCE) */}
      <Input
        value={unit.name}
        maxLength={MAX_UNIT_NAME_LENGTH}
        onChange={(e) => onNameChange(index, e.target.value)}
        placeholder="Name (person or management) — group title on the website"
        className="h-8 mb-2"
        aria-label={`Name (unit ${index + 1})`}
      />

      {/* Field rows — all 4 always present; drag the grip to reorder.
          Unfilled fields are simply skipped on the website (empty rows
          dropped server-side). */}
      <div className="space-y-2">
        <DndContext sensors={fieldSensors} collisionDetection={closestCenter} onDragEnd={handleFieldDragEnd}>
          <SortableContext items={unit.fields.map((f) => f.rowId)} strategy={verticalListSortingStrategy}>
            {unit.fields.map((row) => (
              <SortableFieldRow
                key={row.rowId}
                row={row}
                unitIndex={index}
                onValueChange={onFieldValueChange}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

const ContactSectionManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'details' | 'contact-form'>('details');

  // ── Contact info block state ──
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heading, setHeading] = useState(DEFAULT_CONTACT_HEADING);
  const [description, setDescription] = useState('');
  const [unitRows, setUnitRows] = useState<UnitRow[]>([]);
  const [showInfo, setShowInfo] = useState(true);
  const [savedDoc, setSavedDoc] = useState<ContactInfo | null>(null);
  // Unit-level DnD sensors; field-level DndContexts (inside unit cards) carry
  // their own sensors.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── Contact form state ──
  const [showForm, setShowForm] = useState(true);
  const [enquiryEmail, setEnquiryEmail] = useState('');
  const [savedEnquiryEmail, setSavedEnquiryEmail] = useState<string | null>(null);
  const [savingForm, setSavingForm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getContactInfo()
      .then((info) => {
        if (cancelled) return;
        setHeading(effectiveHeading(info));
        setDescription(info.description || '');
        setUnitRows(toRows(info.units || []));
        setShowInfo(info.showInfo !== false);
        setShowForm(info.showForm !== false);
        setSavedDoc(info);
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load contact info settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const infoHasChanges =
    !!savedDoc &&
    (heading.trim() !== effectiveHeading(savedDoc) ||
      description !== (savedDoc.description || '') ||
      showInfo !== (savedDoc.showInfo !== false) ||
      JSON.stringify(toPayload(unitRows)) !== JSON.stringify(savedDoc.units || []));

  const handleSaveInfo = async () => {
    if (!heading.trim()) {
      toast.error('Contact information heading cannot be empty');
      return;
    }
    // Client-side validation mirroring backend rules
    const problems: string[] = [];
    unitRows.forEach((unit, i) => {
      unit.fields.forEach((row) => {
        if (row.value.length > MAX_UNIT_FIELD_LENGTH) {
          problems.push(`Unit ${i + 1}: ${UNIT_FIELD_META[row.type].label} is too long`);
        }
      });
    });
    if (problems.length) {
      toast.error(problems[0]);
      return;
    }

    setSaving(true);
    try {
      const saved = await updateContactInfo({
        heading: heading.trim().slice(0, MAX_HEADING_LENGTH),
        description: description.slice(0, MAX_DESCRIPTION_LENGTH),
        units: toPayload(unitRows).map((u) => ({
          name: u.name.trim().slice(0, MAX_UNIT_NAME_LENGTH),
          fields: u.fields.map((f) => ({ ...f, value: f.value.trim().slice(0, MAX_UNIT_FIELD_LENGTH) })),
        })),
        showInfo,
      });
      setHeading(effectiveHeading(saved));
      setDescription(saved.description || '');
      setUnitRows(toRows(saved.units || []));
      setShowInfo(saved.showInfo !== false);
      setSavedDoc(saved);
      toast.success('Contact info saved — website updates in a moment');
    } catch (err: any) {
      const firstError = Array.isArray(err.response?.data?.errors) ? err.response.data.errors[0] : null;
      toast.error(firstError || err.response?.data?.message || 'Failed to save contact info');
    } finally {
      setSaving(false);
    }
  };

  const handleResetInfo = () => {
    if (!savedDoc) return;
    setHeading(effectiveHeading(savedDoc));
    setDescription(savedDoc.description || '');
    setUnitRows(toRows(savedDoc.units || []));
    setShowInfo(savedDoc.showInfo !== false);
  };

  const handleAddUnit = () => {
    if (unitRows.length >= MAX_CONTACT_UNITS) return;
    // Reuse toRows so new units get all 4 field rows, same as on load.
    setUnitRows([...unitRows, ...toRows([{ name: '', fields: [] }])]);
  };

  const handleRemoveUnit = (index: number) => {
    setUnitRows(unitRows.filter((_, i) => i !== index));
  };

  const handleUnitNameChange = (index: number, value: string) => {
    setUnitRows(unitRows.map((u, i) => (i === index ? { ...u, name: value } : u)));
  };

  const handleFieldValueChange = (unitIndex: number, rowId: string, value: string) => {
    setUnitRows(unitRows.map((u, i) =>
      i === unitIndex ? { ...u, fields: u.fields.map((f) => (f.rowId === rowId ? { ...f, value } : f)) } : u
    ));
  };

  /** Field reorder INSIDE a unit — local only, persisted via Save. */
  const handleFieldReorder = (unitIndex: number, oldIndex: number, newIndex: number) => {
    setUnitRows(unitRows.map((u, i) =>
      i === unitIndex ? { ...u, fields: arrayMove(u.fields, oldIndex, newIndex) } : u
    ));
  };

  /** Unit reorder — local only, persisted via Save Contact Details. */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setUnitRows((rows) => {
      const oldIndex = rows.findIndex((r) => r.id === active.id);
      const newIndex = rows.findIndex((r) => r.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return rows;
      return arrayMove(rows, oldIndex, newIndex);
    });
  };

  /** Contact-form tab dirty flag — show/hide toggle + enquiry email. */
  const formHasChanges =
    (!!savedDoc && showForm !== (savedDoc.showForm !== false)) ||
    enquiryEmail.trim() !== (savedEnquiryEmail || '');

  const handleSaveForm = async () => {
    const trimmed = enquiryEmail.trim();
    const emailChanged = trimmed !== (savedEnquiryEmail || '');
    if (emailChanged && trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Please enter a valid email address');
      return;
    }
    const formChanged = !!savedDoc && showForm !== (savedDoc.showForm !== false);
    if (!emailChanged && !formChanged) return;

    setSavingForm(true);
    try {
      if (formChanged) {
        // Partial save — flips showForm without touching the details block
        const saved = await updateContactInfo({ showForm });
        setSavedDoc(saved);
      }
      if (emailChanged) {
        const res = await updateLeadNotificationEmail(trimmed);
        setSavedEnquiryEmail(res.leadNotificationEmail);
        setEnquiryEmail(res.leadNotificationEmail || '');
      }
      toast.success('Contact form settings saved — website updates in a moment');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save contact form settings');
    } finally {
      setSavingForm(false);
    }
  };

  const handleResetForm = () => {
    setShowForm(savedDoc ? savedDoc.showForm !== false : true);
    setEnquiryEmail(savedEnquiryEmail || '');
  };

  return (
    <div>
      <div className="mb-4">
        <h2 className="page-title">Contact Section</h2>
        {/* <p className="text-sm text-muted-foreground mt-1">
          Manage how the contact section looks on your website.
        </p> */}
      </div>

      <SectionTextSettingsCard sectionKey="contact"  />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'details' | 'contact-form')}>
        <TabsList>
          <TabsTrigger value="details">Contact Details</TabsTrigger>
          <TabsTrigger value="contact-form">Contact Form</TabsTrigger>
        </TabsList>

        {/* ── Contact details tab ── */}
        <TabsContent value="details" className="mt-4">
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 text-sm font-medium bg-muted/40">
              <span>Contact details block</span>
              <span className={`text-[10px] font-mono ${infoHasChanges ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                {infoHasChanges ? 'Unsaved changes' : 'Saved'}
              </span>
            </div>

            <div className="px-4 py-4 border-t border-border/50">
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading contact details…</p>
              ) : (
                <div className="space-y-5 max-w-2xl">
                  {/* Show / hide toggle */}
                  <div className="flex items-center justify-between rounded-lg border border-border p-3">
                    <div>
                      <Label className="form-label">Show contact information</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Off hides the info column — the contact form then takes the full width.
                      </p>
                    </div>
                    <Switch checked={showInfo} onCheckedChange={setShowInfo} />
                  </div>

                  {/* Block heading — the "Contact Information" title on the website */}
                  <div className="form-group">
                    <Label className="form-label">Heading</Label>
                    <Input
                      value={heading}
                      maxLength={MAX_HEADING_LENGTH}
                      onChange={(e) => setHeading(e.target.value)}
                      placeholder={DEFAULT_CONTACT_HEADING}
                      aria-label="Contact information heading"
                    />
                    <p className="text-xs text-muted-foreground">
                      Title of this block on the website (default: {DEFAULT_CONTACT_HEADING}). {heading.length}/{MAX_HEADING_LENGTH}
                    </p>
                  </div>

                  {/* Description */}
                  <div className="form-group">
                    <Label className="form-label">Description</Label>
                    <Textarea
                      value={description}
                      maxLength={MAX_DESCRIPTION_LENGTH}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      placeholder="Add a short introduction for this block (optional)"
                    />
                    <p className="text-xs text-muted-foreground">
                      Short paragraph shown above the contact entries — leave empty to show none. {description.length}/{MAX_DESCRIPTION_LENGTH}
                    </p>
                  </div>

                  {/* Units editor — each unit = name + ordered field rows */}
                  <div className="form-group">
                    <Label className="form-label">
                      Contact units{' '}
                      <span className="text-muted-foreground font-normal">
                        ({unitRows.length}/{MAX_CONTACT_UNITS}) — drag the grips to reorder units and the fields inside each unit
                      </span>
                    </Label>

                    {unitRows.length === 0 && (
                      <p className="text-xs text-muted-foreground border border-dashed border-border rounded-lg p-3">
                        No contact units yet — add one to start entering phone, email, address or WhatsApp details.
                      </p>
                    )}

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={unitRows.map((u) => u.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-3">
                          {unitRows.map((unit, index) => (
                            <SortableUnitCard
                              key={unit.id}
                              unit={unit}
                              index={index}
                              onNameChange={handleUnitNameChange}
                              onFieldValueChange={handleFieldValueChange}
                              onFieldReorder={handleFieldReorder}
                              onRemove={handleRemoveUnit}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>

                    {/* Add more */}
                    {unitRows.length < MAX_CONTACT_UNITS && (
                      <Button type="button" variant="outline" size="sm" className="mt-3" onClick={handleAddUnit}>
                        <Plus className="size-3.5 mr-1" />
                        Add more
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <Button type="button" loading={saving} disabled={!infoHasChanges} onClick={handleSaveInfo}>
                      Save Contact Details
                    </Button>
                    <Button type="button" variant="tertiary" disabled={!infoHasChanges || saving} onClick={handleResetInfo}>
                      Reset
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ── Contact form tab ── */}
        <TabsContent value="contact-form" className="mt-4">
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 text-sm font-medium bg-muted/40">
              <span>Contact form</span>
              <span className={`text-[10px] font-mono ${formHasChanges ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                {formHasChanges ? 'Unsaved changes' : 'Saved'}
              </span>
            </div>
            <div className="px-4 py-4 border-t border-border/50">
              <div className="space-y-5 max-w-xl">
                {/* Show / hide toggle — same pattern as "Show contact information" */}
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <Label className="form-label">Show contact form</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Off hides the enquiry form on your website and stops accepting new submissions.
                    </p>
                  </div>
                  <Switch checked={showForm} onCheckedChange={setShowForm} />
                </div>

                {/* Enquiry email */}
                <div className="form-group">
                  <Label className="form-label">Send new enquiries to</Label>
                  <Input
                    type="email"
                    value={enquiryEmail}
                    onChange={(e) => setEnquiryEmail(e.target.value)}
                    placeholder="bookings@artist.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    Where new-lead notification emails are sent. Leave empty to use your account email
                    {savedEnquiryEmail ? ` (currently overriding: ${savedEnquiryEmail})` : ''}. Turn notifications on/off via the toggle in{' '}
                    <span className="font-medium text-foreground">Website Leads</span> (Pro Max feature).
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <Button type="button" loading={savingForm} disabled={!formHasChanges} onClick={handleSaveForm}>
                    Save Contact Form
                  </Button>
                  <Button type="button" variant="tertiary" disabled={!formHasChanges || savingForm} onClick={handleResetForm}>
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContactSectionManager;
